/**
 * The device half of the compute step: a formula's answer, worked out here.
 *
 * Section 5.7 rule 6, which Section 5.5 inherits: "the device runs the same
 * rule". The rule is `evaluateFormula` in `@alluzo/shared`, imported by this
 * file and by `apps/server/src/formula-fill.ts`, so there is one implementation
 * and not two that must agree.
 *
 * WHY THE PHONE COMPUTES AT ALL. A save is offline first. Without this the
 * Amount on a sale saved on a train would be blank until the next pull, and the
 * day total on the home screen would be short by every row still pending. The
 * server recomputes on sync and the server value wins, exactly as the doc says.
 *
 * WHAT NEVER LEAVES. The outbox carries the TYPED values only. The server
 * refuses a written value on a worked-out field, and it is right to: the recipe
 * is the truth, and a device that could post an answer could post a wrong one.
 * `withoutComputed` is what the push path calls.
 */
import {
  blocksOnForm,
  checkRules,
  evaluateFormula,
  formulaHopsRead,
  localDayOf,
  type BrokenRule,
  formulaLinesRead,
  formulaOrder,
  type EntryLinesWrite,
  type EntryValues,
  type FormulaSpec,
  type LineWrite,
  type FormulaValues,
  type LinesReader,
  type RecordSchemaResponse,
  type RollupSpec,
  formulaFieldsRead,
  formulaOf,
  rollupBeats,
  rollupCounts,
  rollupOf,
  rollupValueOf,
} from '@alluzo/shared';
import { and, eq, inArray, isNull } from 'drizzle-orm';

import { getDb } from '@/lib/db';
import { entryMirror } from '@/lib/db-schema';
import { lineContext, withoutLineRefs, type LineAbove, type RowAbove } from '@/lib/line-above';
import { choiceKeysOf } from '@/lib/mirror-rows';

/** Every field on this record whose value is worked out from a recipe. */
const formulasOf = (schema: RecordSchemaResponse): readonly { id: string; formula: FormulaSpec }[] =>
  schema.fields.flatMap((field) => {
    const formula = field.type === 'formula' ? formulaOf(field.spec) : null;
    return formula === null ? [] : [{ id: field.id, formula }];
  });

/** True when this record has nothing to work out, which is the common case. */
export const hasFormulas = (schema: RecordSchemaResponse): boolean =>
  formulasOf(schema).length > 0;

/** The chosen row ids a multi_select value holds, in the person's order. */
const chosenIds = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string' && one !== '') : [];

/**
 * The values of a set of mirrored rows, keyed by entry id.
 *
 * One query, whatever the number of hops. A row this device has not pulled yet
 * is simply absent, and the formula that wanted it answers nothing rather than
 * a wrong number.
 */
const valuesForEntryIds = (ids: readonly string[]): Map<string, EntryValues> => {
  const out = new Map<string, EntryValues>();
  if (ids.length === 0) return out;

  const rows = getDb()
    .select({ entryId: entryMirror.entryId, valuesJson: entryMirror.valuesJson })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.entryId, [...ids]),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();

  for (const row of rows) {
    try {
      out.set(row.entryId, JSON.parse(row.valuesJson) as EntryValues);
    } catch {
      // A row whose document will not parse is skipped, not thrown. One bad
      // mirror row must never stop a person saving a sale.
    }
  }
  return out;
};

/**
 * What a form of lines hands the fill beside the row's own values (Decisions
 * 6 and 8 of the lines plan), the same two things the server's
 * `FormulaContext` carries.
 */
export type FormulaFillContext = {
  /**
   * Rows a hop may land on that the form itself holds, keyed by entry id: a
   * line's parent and root, which are not in the mirror while the entry is
   * unsaved. A hop to one of them reads the form's values, never the mirror.
   */
  readonly known?: ReadonlyMap<string, FormulaValues>;
  /** The lines directly under this row, by `lines` field id, each already worked out. */
  readonly childLines?: ReadonlyMap<string, readonly FormulaValues[]>;
};

const NO_KNOWN: ReadonlyMap<string, FormulaValues> = new Map();
const NO_LINES: ReadonlyMap<string, readonly FormulaValues[]> = new Map();

/**
 * The same values, with every formula on the record answered.
 *
 * The answers are folded in on top, so a caller cannot accidentally supply one:
 * whatever was passed for a formula field is replaced by what the recipe says.
 *
 * THE ORDER MATTERS, and it is not the order the fields happen to be in. A
 * formula may read another formula on the same row — a Total that reads an
 * Amount that reads the customer's usual rate — so an answer has to be worked
 * out before the formula that reads it, and it has to be fed into the picture
 * that one reads. `formulaOrder` in `@alluzo/shared` decides the order, and
 * `applyFormulas` on the server sorts by the very same function: one
 * implementation, so the two sides cannot disagree about what a row says.
 *
 * A ring answers NOTHING. `checkFormula` refuses one when the field is made, so
 * a ring on a device can only be a spec written by an older build or edited by
 * hand; the fields in it are set to null and the rest are still filled. Half a
 * row of right answers beats a row of guesses.
 */
export const fillFormulas = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  context: FormulaFillContext = {},
): EntryValues => {
  const formulas = formulasOf(schema);
  if (formulas.length === 0) return values;
  const known = context.known ?? NO_KNOWN;
  const childLines = context.childLines ?? NO_LINES;
  // A `lines` field stores nothing on the row: its rows are the lines under
  // it, handed in. A multi_select holds the ids of its chosen rows.
  const linesFields = new Set(schema.fields.filter((field) => field.type === 'lines').map((field) => field.id));

  // Only the ids a hop names, and the rows an `agg` totals a field over, so a
  // record whose formulas reach no other row touches the database not at all.
  // A count reads no field, so it counts the ids and loads nothing: the server
  // counts the same ids.
  const pointed = new Set<string>();
  for (const field of formulas) {
    for (const node of formulaHopsRead(field.formula)) {
      const held = values[node.via];
      if (typeof held === 'string' && held !== '' && !known.has(held)) pointed.add(held);
    }
    for (const read of formulaLinesRead(field.formula)) {
      if (read.fields.length === 0 || linesFields.has(read.lines)) continue;
      for (const id of chosenIds(values[read.lines])) pointed.add(id);
    }
  }
  const rows = valuesForEntryIds([...pointed]);
  const readHop = (entryId: string, fieldId: string): unknown =>
    known.has(entryId) ? known.get(entryId)?.[fieldId] : rows.get(entryId)?.[fieldId];

  const held = new Map<string, FormulaSpec>(
    formulas.map((field) => [field.id, field.formula]),
  );
  const listed = formulas.map((field) => ({ fieldId: field.id, spec: field.formula }));

  const first = formulaOrder(listed);
  const ringed = first.ok ? new Set<string>() : new Set(first.cycle);
  // Whatever is left once the ring is taken out has no ring of its own, so the
  // second pass always answers an order.
  const sorted = first.ok
    ? first
    : formulaOrder(listed.filter((one) => !ringed.has(one.fieldId)));
  const order = sorted.ok ? sorted.order : [];

  const out: Record<string, unknown> = { ...values };
  // One row per chosen id, in the person's order. A row this device has not
  // pulled holds nothing, and the total follows `onMissing` as for any empty field.
  // A `lines` field reads the lines handed in, and none when none were.
  const readLines: LinesReader = (linesFieldId) =>
    linesFields.has(linesFieldId)
      ? (childLines.get(linesFieldId) ?? [])
      : chosenIds(out[linesFieldId]).map((entryId) => rows.get(entryId) ?? {});
  for (const fieldId of ringed) out[fieldId] = null;
  // A `date_add` whose unit is a select reads the choice's key (F4).
  const optionKey = choiceKeysOf(schema);
  for (const fieldId of order) {
    // `out`, not `values`: the answer just worked out is part of the picture
    // the next formula reads.
    out[fieldId] = evaluateFormula(held.get(fieldId)!, out as FormulaValues, readHop, readLines, { optionKey });
  }
  return out as EntryValues;
};

/**
 * True when anything in the tree works something out: the row itself, or a
 * line at any depth under it. A record with no formula of its own can still
 * hold lines that have one.
 */
export const treeHasFormulas = (schema: RecordSchemaResponse): boolean =>
  hasFormulas(schema) || Object.values(schema.lineSchemas ?? {}).some(treeHasFormulas);

/**
 * An entry and every line under it, with every formula answered, in the order
 * Decision 8 of docs/plans/entry-lines-and-layout.md fixes: the deepest lines
 * first, then the lines above them, then the entry. So an `agg` on a row only
 * ever totals lines that are already worked out, and a stay's Total guests sums
 * each room's own Guests count.
 *
 * `lines` is what the form holds, keyed by `lines` field id, each line a
 * `LineWrite` (its device-made id, its typed values, and its own lines). A
 * `lines` field the form does not hand in counts as none, so a form must hand
 * in every run it draws, edited or not.
 *
 * A line reads up the tree through its parent and root fields (Decision 6).
 * Neither row is in the mirror while the entry is unsaved, so each hop is
 * answered from the form's values (`lineContext`), and the two ids are taken
 * out again before the line is handed back. A line formula may read only the
 * TYPED fields above it (`FORMULA_PARENT_COMPUTED`), so the row above is read
 * as the person left it, and the order cannot ring.
 *
 * The same order runs on the server inside the write, and the server's answer
 * wins, as it does for a row with no lines.
 */
export const fillFormulaTree = (
  schema: RecordSchemaResponse,
  entry: RowAbove,
  lines: EntryLinesWrite,
): Readonly<{ values: EntryValues; lines: EntryLinesWrite }> => {
  const under = fillRuns(schema, lines, { parent: entry, root: entry });
  return {
    values: fillFormulas(schema, entry.values, { childLines: under.childLines }),
    lines: under.lines,
  };
};

/** Every run of lines under one row, worked out, and what each total reads. */
const fillRuns = (
  schema: RecordSchemaResponse,
  lines: EntryLinesWrite,
  above: LineAbove,
): Readonly<{ lines: EntryLinesWrite; childLines: ReadonlyMap<string, readonly FormulaValues[]> }> => {
  const lineSchemas = schema.lineSchemas ?? {};
  const out: Record<string, readonly LineWrite[]> = {};
  const childLines = new Map<string, readonly FormulaValues[]>();
  for (const [linesFieldId, run] of Object.entries(lines)) {
    const lineSchema = lineSchemas[linesFieldId];
    // A run whose schema this device does not hold (a cache from before it
    // existed) is handed back as typed, and totalled as it stands.
    const filled = lineSchema === undefined ? run : run.map((line) => fillLine(lineSchema, line, above));
    out[linesFieldId] = filled;
    childLines.set(linesFieldId, filled.map((line) => line.values as FormulaValues));
  }
  return { lines: out, childLines };
};

/** One line: its own lines first, then itself, reading up the tree from the form. */
const fillLine = (lineSchema: RecordSchemaResponse, line: LineWrite, above: LineAbove): LineWrite => {
  const own: RowAbove = { entryId: line.entryId, values: line.values };
  // Its own lines hang from it, and their root is the entry at the top.
  const under = fillRuns(lineSchema, line.lines ?? {}, { parent: own, root: above.root });
  const context = lineContext(lineSchema, line.values, above);
  const values = withoutLineRefs(
    lineSchema,
    fillFormulas(lineSchema, context.values, { known: context.known, childLines: under.childLines }),
  );
  return line.lines === undefined
    ? { entryId: line.entryId, values }
    : { entryId: line.entryId, values, lines: under.lines };
};

/* ------------------------------------------------------------- Must be */

/** A rule that said no on this device, and the line it is on when it is on one. */
export type DeviceBrokenRule = BrokenRule & { readonly lineEntryId?: string };

/**
 * Thrown before an entry enters the outbox when a Must be rule of level
 * `block` says no (F13 and F14 of docs/plans/field-relations.md). Any block
 * stops the save here, local or remote: the device copy of another row may be
 * old, but the form is where the person can still fix it. The server then
 * refuses only a local block, so a refusal never reaches the outbox.
 */
export class RuleBroken extends Error {
  constructor(readonly broken: readonly DeviceBrokenRule[]) {
    super(broken.map((one) => one.message).join(' '));
    this.name = 'RuleBroken';
  }
}

/** A schema as `checkRules` reads it. */
const rulesOf = (schema: RecordSchemaResponse) => ({
  fields: schema.fields.map((field) => ({
    id: field.id,
    label: field.label,
    type: field.type,
    showWhen: field.showWhen ?? null,
    mustBe: field.mustBe ?? null,
    spec: field.spec,
  })),
  rules: schema.rules ?? null,
});

/** True when the schema, or a lines schema under it, keeps a rule. */
const treeHasRules = (schema: RecordSchemaResponse): boolean =>
  (schema.rules ?? []).length > 0 ||
  schema.fields.some((field) => (field.mustBe ?? []).length > 0) ||
  Object.values(schema.lineSchemas ?? {}).some(treeHasRules);

/**
 * Every Must be rule the row and its lines break, worked out on the device:
 * the shared `checkRules`, run last on the values as `fillFormulas` and
 * `fillFormulaTree` left them. A hop reads the mirror, as a formula's does; a
 * line's rule reads the line, its own lines and, through `lineContext`, the
 * rows above it as the form holds them.
 *
 * `lines` is the worked-out tree (`fillFormulaTree(...).lines`), or null when
 * the write holds no lines. `sent`, on an edit, names the lines the edit
 * sends: only their own rules run, because a line the edit keeps as stored is
 * not the person's write (F16); a total over the lines still reads them all.
 */
export const rulesBroken = (
  schema: RecordSchemaResponse,
  entry: RowAbove,
  lines: EntryLinesWrite | null,
  today: string = localDayOf(new Date()),
  sent: ReadonlySet<string> | null = null,
): readonly DeviceBrokenRule[] => {
  if (!treeHasRules(schema)) return [];
  const out: DeviceBrokenRule[] = [];
  const runRow = (
    rowSchema: RecordSchemaResponse,
    values: FormulaValues,
    under: EntryLinesWrite,
    known: ReadonlyMap<string, FormulaValues>,
    lineEntryId: string | undefined,
  ): void => {
    const ruled = rulesOf(rowSchema);
    if ((ruled.rules ?? []).length === 0 && !ruled.fields.some((field) => (field.mustBe ?? []).length > 0)) return;
    const pointed = new Set<string>();
    const specs = [
      ...ruled.fields.flatMap((field) => (field.mustBe ?? []).map((rule) => rule.formula)),
      ...(ruled.rules ?? []).map((rule) => rule.formula),
      ...ruled.fields.flatMap((field) => (field.showWhen === null ? [] : [field.showWhen])),
    ];
    for (const spec of specs) {
      for (const node of formulaHopsRead(spec)) {
        const held = values[node.via];
        if (typeof held === 'string' && held !== '' && !known.has(held)) pointed.add(held);
      }
    }
    const rows = valuesForEntryIds([...pointed]);
    const readHop = (entryId: string, fieldId: string): unknown =>
      known.has(entryId) ? known.get(entryId)?.[fieldId] : rows.get(entryId)?.[fieldId];
    const linesFields = new Set(rowSchema.fields.filter((field) => field.type === 'lines').map((field) => field.id));
    const readLines: LinesReader = (linesFieldId) =>
      linesFields.has(linesFieldId) ? (under[linesFieldId] ?? []).map((line) => line.values as FormulaValues) : [];
    for (const one of checkRules(ruled, values, readLines, readHop, { today, optionKey: choiceKeysOf(rowSchema) })) {
      out.push(lineEntryId === undefined ? one : { ...one, lineEntryId });
    }
  };
  const runLines = (rowSchema: RecordSchemaResponse, under: EntryLinesWrite, above: LineAbove): void => {
    for (const [linesFieldId, run] of Object.entries(under)) {
      const lineSchema = rowSchema.lineSchemas?.[linesFieldId];
      if (lineSchema === undefined) continue;
      for (const line of run) {
        const own: RowAbove = { entryId: line.entryId, values: line.values };
        const context = lineContext(lineSchema, line.values, above);
        if (sent === null || sent.has(line.entryId)) runRow(lineSchema, context.values, line.lines ?? {}, context.known, line.entryId);
        runLines(lineSchema, line.lines ?? {}, { parent: own, root: above.root });
      }
    }
  };
  runRow(schema, entry.values, lines ?? {}, new Map(), undefined);
  if (lines !== null) runLines(schema, lines, { parent: entry, root: entry });
  return out;
};

/** Throws `RuleBroken` when any rule of the write blocks a save on the form (F14). */
export const holdRules = (
  schema: RecordSchemaResponse,
  entry: RowAbove,
  lines: EntryLinesWrite | null,
  today?: string,
  sent: ReadonlySet<string> | null = null,
): void => {
  const blocking = rulesBroken(schema, entry, lines, today, sent).filter(blocksOnForm);
  if (blocking.length > 0) throw new RuleBroken(blocking);
};

/**
 * The values a push may carry: everything a person typed, and no answer.
 *
 * The server works the answers out again from the recipe. Sending one would be
 * refused with FIELD_COMPUTED, which is the right refusal: an answer the device
 * could post is an answer the device could get wrong.
 */
export const withoutComputed = (
  schema: RecordSchemaResponse,
  values: EntryValues,
): EntryValues => {
  const computed = new Set(
    schema.fields
      .filter((field) => field.type === 'formula' || field.type === 'rollup' || field.type === 'lookup')
      .map((field) => field.id),
  );
  if (computed.size === 0) return values;
  const out: Record<string, unknown> = {};
  for (const [fieldId, value] of Object.entries(values)) {
    if (computed.has(fieldId)) continue;
    out[fieldId] = value;
  }
  return out as EntryValues;
};

/**
 * The lines a push may carry: what a person typed on each line, at both
 * levels, and no answer and no parent or root id.
 *
 * `withoutComputed` for a tree. The server works every line formula out again
 * inside the write, deepest first, and makes the parent and root of every
 * line itself (Decision 3), so a device that sent either would be sending a
 * value that is not its to send. A run whose schema the device does not hold
 * goes as it is: the server checks it against the schema it does hold.
 */
export const withoutComputedLines = (
  schema: RecordSchemaResponse,
  lines: EntryLinesWrite,
): EntryLinesWrite => {
  const lineSchemas = schema.lineSchemas ?? {};
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [linesFieldId, run] of Object.entries(lines)) {
    const lineSchema = lineSchemas[linesFieldId];
    out[linesFieldId] =
      lineSchema === undefined
        ? run
        : run.map((line) => {
            const values = withoutLineRefs(lineSchema, withoutComputed(lineSchema, line.values));
            return line.lines === undefined
              ? { entryId: line.entryId, values }
              : { entryId: line.entryId, values, lines: withoutComputedLines(lineSchema, line.lines) };
          });
  }
  return out;
};

/* ------------------------------------------------------- rollups while waiting */

/** Every rollup on this record, with its spec. */
const rollupsOf = (schema: RecordSchemaResponse): readonly { id: string; spec: RollupSpec }[] =>
  schema.fields.flatMap((field) => {
    const spec = field.type === 'rollup' ? rollupOf(field.spec) : null;
    return spec === null ? [] : [{ id: field.id, spec }];
  });

/** True when this record keeps a total of other entries (the room bill). */
export const hasRollups = (schema: RecordSchemaResponse): boolean => rollupsOf(schema).length > 0;

/** One row this device wrote that the server has not answered yet, of a record a rollup totals. */
export type WaitingRow = {
  readonly entryId: string;
  readonly recordId: string;
  /** 0 until the server acks: a create. Higher: an edit of a row the server already counts. */
  readonly version: number;
  readonly values: EntryValues;
};

/**
 * A row as the list draws it with what is waiting in the outbox laid over its
 * rollups (Phase 4.7 of docs/plans/playbooks-and-room-bill.md). `unsaved`
 * names the fields whose figure holds an entry the server has not saved yet:
 * the rollup and every formula that reads it.
 */
export type WithWaiting<R> = R & { readonly unsaved?: readonly string[] };

/**
 * The server's rollup answer, plus the orders still waiting in the outbox.
 *
 * The server keeps the bill (R12) and its answer wins, so the row's own value
 * is the starting point. An order this device made and has not pushed (a
 * create) is added to the booking it picks when it counts (`rollupCounts`;
 * a typed row is approved), and the formulas that read the bill are worked out
 * again over it, so the Grand Total and the Balance follow at once. An edit
 * still waiting is a row the server already counts as it was, and the device
 * no longer holds what it was, so its booking keeps the server's figure and is
 * only marked as not yet saved. Pure: the waiting rows are handed in.
 */
export const withWaitingRollups = <R extends { readonly entryId: string; readonly values: EntryValues }>(
  schema: RecordSchemaResponse,
  rows: readonly R[],
  waiting: readonly WaitingRow[],
): readonly WithWaiting<R>[] => {
  const rollups = rollupsOf(schema);
  if (rollups.length === 0 || waiting.length === 0) return rows;

  // The formulas that read a rollup, directly or through another formula: the
  // only ones worked out again here. A total over lines is not touched, so it
  // never reads lines this list does not hold.
  const reading = new Set(rollups.map((one) => one.id));
  for (let moved = true; moved; ) {
    moved = false;
    for (const field of formulasOf(schema)) {
      if (reading.has(field.id)) continue;
      if (formulaFieldsRead(field.formula).some((id) => reading.has(id))) {
        reading.add(field.id);
        moved = true;
      }
    }
  }
  const rerun: RecordSchemaResponse = {
    ...schema,
    fields: schema.fields.filter((field) => field.type !== 'formula' || reading.has(field.id)),
  };

  return rows.map((row) => {
    const values: Record<string, unknown> = { ...row.values };
    let touched = false;
    for (const rollup of rollups) {
      const { agg } = rollup.spec;
      let add = 0;
      // A min or a max moves only when a waiting entry beats what the server holds (F18).
      let best: number | string | null = agg === 'min' || agg === 'max' ? rollupValueOf(rollup.spec, values[rollup.id]) : null;
      let beaten = false;
      for (const one of waiting) {
        if (one.recordId !== rollup.spec.over || one.values[rollup.spec.via] !== row.entryId) continue;
        touched = true;
        if (one.version !== 0 || !rollupCounts(rollup.spec, one.values, null)) continue;
        if (agg === 'count') add += 1;
        else if (agg === 'min' || agg === 'max') {
          const read = rollupValueOf(rollup.spec, one.values[rollup.spec.of ?? '']);
          if (read !== null && rollupBeats(agg, best, read)) {
            best = read;
            beaten = true;
          }
        } else {
          const amount = Number(one.values[rollup.spec.of ?? '']);
          if (Number.isFinite(amount)) add += amount;
        }
      }
      if (beaten) values[rollup.id] = best;
      if (add !== 0) {
        const held = Number(values[rollup.id] ?? 0);
        values[rollup.id] = (Number.isFinite(held) ? held : 0) + add;
      }
    }
    if (!touched) return row;
    return { ...row, values: fillFormulas(rerun, values as EntryValues), unsaved: [...reading] };
  });
};

/**
 * The rows of the records these rollups total that this device wrote and the
 * server has not answered yet, read off the mirror in one query.
 */
export const waitingRowsFor = (schema: RecordSchemaResponse): readonly WaitingRow[] => {
  const overs = [...new Set(rollupsOf(schema).map((one) => one.spec.over))];
  if (overs.length === 0) return [];
  const rows = getDb()
    .select({
      entryId: entryMirror.entryId,
      recordId: entryMirror.recordId,
      version: entryMirror.version,
      valuesJson: entryMirror.valuesJson,
    })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.recordId, overs),
        eq(entryMirror.pending, true),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();
  return rows.flatMap((row) => {
    try {
      return [{ entryId: row.entryId, recordId: row.recordId, version: row.version, values: JSON.parse(row.valuesJson) as EntryValues }];
    } catch {
      return [];
    }
  });
};
